import { z } from 'zod';

export const ALLOWED_INPUT_ATTACHMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  // 'audio/mpeg',
  // 'video/mp4',
  'text/csv',
  'image/jpeg',
  'image/png',
] as const;

export const INPUT_ATTACHMENT_MAX_FILES_PER_MESSAGE = 5;

export const INPUT_ATTACHMENT_MAX_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB

// stored in DB, has fileUri, no url
export const InputAttachmentSchema = z.object({
  type: z.literal('file'),
  identifier: z.uuid(),
  fileUri: z.string(),
  name: z.string(),
  mimeType: z.enum(ALLOWED_INPUT_ATTACHMENT_MIME_TYPES),
  sizeBytes: z.number().int().positive().max(INPUT_ATTACHMENT_MAX_SIZE_BYTES),
  checksum: z.string().optional(),
});

export type InputAttachment = z.infer<typeof InputAttachmentSchema>;

// sent to DS agent, has url, no fileUri
export const InputAttachmentPayloadSchema = z.object({
  type: z.literal('file'),
  identifier: z.uuid(),
  url: z.url(),
  name: z.string(),
  mimeType: z.enum(ALLOWED_INPUT_ATTACHMENT_MIME_TYPES),
  sizeBytes: z.number().int().positive().max(INPUT_ATTACHMENT_MAX_SIZE_BYTES),
});

export type InputAttachmentPayload = z.infer<typeof InputAttachmentPayloadSchema>;

// Response shape for a single file from the upload-credentials endpoint
export const UploadCredentialSchema = z.object({
  identifier: z.string(),
  credentials: z.object({
    accessKeyId: z.string(),
    secretAccessKey: z.string(),
    sessionToken: z.string(),
    expiration: z.string(),
  }),
  bucket: z.string(),
  s3Key: z.string(),
  region: z.string(),
});

export type UploadCredential = z.infer<typeof UploadCredentialSchema>;

export const DOCUMENT_INPUT_ATTACHMENT_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
] as const;

export type DocumentInputAttachmentMimeType = (typeof DOCUMENT_INPUT_ATTACHMENT_MIME_TYPES)[number];

// 20 MB file upload limit
export const DOCUMENT_INPUT_ATTACHMENT_MAX_SIZE_BYTES = 20 * 1024 * 1024;

// Ceiling on input attachment text extraction
export const INPUT_ATTACHMENT_EXTRACTION_MAX_TOKENS = 200_000;

// Below this, extracted text is inlined verbatim into the orchestrator's context.
// Above it, only a <attachment identifier="..."/> reference is inlined and the
// orchestrator calls ask-uploaded-file with the identifier to read it.
export const INPUT_ATTACHMENT_VERBATIM_MAX_TOKENS = 5_000;

export function isDocumentInputAttachmentMimeType(
  mimeType: string
): mimeType is DocumentInputAttachmentMimeType {
  return (DOCUMENT_INPUT_ATTACHMENT_MIME_TYPES as readonly string[]).includes(mimeType);
}

export function maxInputAttachmentSizeBytes(mimeType: string): number {
  return isDocumentInputAttachmentMimeType(mimeType)
    ? DOCUMENT_INPUT_ATTACHMENT_MAX_SIZE_BYTES
    : INPUT_ATTACHMENT_MAX_SIZE_BYTES;
}

export const INPUT_ATTACHMENT_EXTRACTION_ERRORS = [
  'too_large',
  'no_text',
  'garbled',
  'unsupported',
  'parse_failed',
] as const;

export type InputAttachmentExtractionError = (typeof INPUT_ATTACHMENT_EXTRACTION_ERRORS)[number];

export const INPUT_ATTACHMENT_EXTRACTION_STATUSES = ['pending', 'ready', 'failed', 'not_found'] as const;

export const InputAttachmentExtractionStatusSchema = z.object({
  identifier: z.string(),
  status: z.enum(INPUT_ATTACHMENT_EXTRACTION_STATUSES),
  error: z.enum(INPUT_ATTACHMENT_EXTRACTION_ERRORS).nullable(),
});

export type InputAttachmentExtractionStatus = z.infer<typeof InputAttachmentExtractionStatusSchema>;
